import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';

/**
 * Los endpoints de la regla de cruce paid media ↔ GA4 (`contrato.md` § 2 y § 3). Todos responden con
 * `OKResponse`: quien llama guarda con `isOk()`.
 *
 * **Un 404 es «esto no existe acá», no un error**: el backend viejo no tiene las rutas (el FE puede
 * llegar antes), y el nuevo contesta el mismo 404 a una cuenta ajena o a un usuario del portal. En los
 * dos casos la pantalla no dibuja nada, así que se devuelve `{absent: true}` y cada pieza decide que no
 * existe. El `APIClient` pone `http_status` también cuando el 404 viene en HTML (ruta que Django no
 * conoce), así que la marca alcanza para los dos.
 */

export const ABSENT = Object.freeze({absent: true});

function absentOn404(response) {
    if (response && response.http_status === 404) return ABSENT;
    return response;
}

/** ¿El backend dijo que este usuario no puede escribir? (403 de un miembro que no es admin). */
export function isForbidden(response) {
    return Boolean(response) && (response.http_status === 403 || response.error_code === 'FORBIDDEN');
}

const agencyRulesUrl = () => `${API_URL}agency/ga4-attribution-rules`;
const accountUrl = (clientAdAccountId) => `${API_URL}client/ad_account/${clientAdAccountId}`;

export function fetchAgencyRules() {
    return new APIClient(agencyRulesUrl()).call('GET').then(absentOn404);
}

/** Guarda la regla de UNA plataforma. Responde el mismo shape del GET. */
export function saveAgencyRule(channel, rule) {
    const body = {adgroup_param: rule.adgroup_param, ad_param: rule.ad_param, match_by: rule.match_by};
    return new APIClient(`${agencyRulesUrl()}/${channel}`).call('PUT', body).then(absentOn404);
}

// ⚠️ Ninguna ruta de la cuenta puede empezar con `edit`: la del límite diario
// (`^client/ad_account/(?P<id>\d+)/edit`, sin `$`) se la comería.
export function fetchAccountRule(clientAdAccountId) {
    return new APIClient(`${accountUrl(clientAdAccountId)}/ga4-attribution-rule`).call('GET').then(absentOn404);
}

/** `body` = `{mode: 'agency'}` o `{mode: 'custom', rule: {adgroup_param, ad_param, match_by}}`. */
export function saveAccountRule(clientAdAccountId, body) {
    return new APIClient(`${accountUrl(clientAdAccountId)}/ga4-attribution-rule`).call('PUT', body).then(absentOn404);
}

/**
 * La vista previa de una regla sobre los últimos 30 días de la propiedad GA4 del cliente. Sin `rule`,
 * el backend evalúa la regla efectiva de la cuenta (es lo que se hace con Google, que no tiene
 * parámetros que mandar).
 */
export function previewAccountRule(clientAdAccountId, {propertyId, rule} = {}) {
    const body = {};
    if (propertyId) body.property_id = String(propertyId);
    if (rule) body.rule = {adgroup_param: rule.adgroup_param, ad_param: rule.ad_param, match_by: rule.match_by};
    return new APIClient(`${accountUrl(clientAdAccountId)}/ga4-attribution-preview`).call('POST', body).then(absentOn404);
}
